import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaChevronDown, 
  FaChevronUp, 
  FaBriefcase, 
  FaGraduationCap, 
  FaArrowRight,
  FaUsers,
  FaRocket,
  FaHeart,
  FaGlobeAfrica,
  FaMapMarkerAlt,
  FaClock,
  FaBuilding
} from 'react-icons/fa';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';

const positions = [
  {
    title: 'Senior Software Engineer',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    posted: '2 days ago',
    description: 'We are looking for an experienced software engineer to join our development team.',
    department: 'Engineering',
    experience: '5+ years'
  },
  {
    title: 'ERP Consultant',
    location: 'Remote',
    type: 'Full-time',
    posted: '5 days ago',
    description: 'Seeking an ERP consultant with experience in Microsoft Dynamics 365 implementation.',
    department: 'Consulting',
    experience: '3+ years'
  },
  {
    title: 'Business Analyst',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    posted: '1 week ago',
    description: 'Join our team as a business analyst to help bridge the gap between clients and development.',
    department: 'Analysis',
    experience: '2+ years'
  },
  {
    title: 'DevOps Engineer',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    posted: '1 week ago',
    description: 'Looking for a DevOps engineer to manage our cloud infrastructure and deployment pipelines.',
    department: 'Engineering',
    experience: '3+ years'
  },
  {
    title: 'UI/UX Designer',
    location: 'Nairobi, Kenya',
    type: 'Full-time',
    posted: '2 weeks ago',
    description: 'We need a talented UI/UX designer to create beautiful and intuitive user interfaces.',
    department: 'Design',
    experience: '2+ years'
  },
];

export default function Careers() {
  const [expandedPosition, setExpandedPosition] = useState(null);

  const toggleExpand = (index) => {
    setExpandedPosition(expandedPosition === index ? null : index);
  };

  return (
    <div className="py-16 md:py-20 bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-micropoint-gold/10 text-micropoint-gold px-4 py-1 rounded-full text-sm font-semibold mb-3">
              Careers
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 font-display">
              Join the <span className="text-micropoint-gold">Micropoint</span> Team
            </h1>
            <div className="w-20 h-1 bg-micropoint-gold mx-auto mt-4 rounded-full" />
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-sm md:text-base">
              Build your career with us and help shape the future of digital transformation in Africa.
            </p>
          </motion.div>
        </div>

        {/* Why Work With Us */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {[
            { icon: FaBriefcase, title: 'Innovate', description: 'Work on cutting-edge technology', color: 'from-amber-500 to-orange-500' },
            { icon: FaGraduationCap, title: 'Grow', description: 'Continuous learning & development', color: 'from-blue-500 to-indigo-500' },
            { icon: FaRocket, title: 'Impact', description: 'Solve real problems for Africa', color: 'from-emerald-500 to-teal-500' },
            { icon: FaUsers, title: 'Culture', description: 'Diverse, inclusive environment', color: 'from-purple-500 to-pink-500' },
          ].map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center border border-gray-200 hover:shadow-xl transition-all hover:border-micropoint-gold group"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all shadow-sm`}>
                  <Icon className="text-2xl text-white" />
                </div>
                <h4 className="font-bold text-gray-900 text-lg">{item.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Open Positions */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-micropoint-gold rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-display">Open Positions</h2>
            <span className="ml-2 bg-micropoint-gold/10 text-micropoint-gold text-sm px-3 py-1 rounded-full font-medium">
              {positions.length} roles
            </span>
          </div>
          
          <div className="space-y-4">
            {positions.map((position, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-6 hover:border-micropoint-gold transition-all hover:shadow-md">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900">{position.title}</h3>
                        <span className="text-xs bg-micropoint-gold/10 text-micropoint-gold px-2.5 py-0.5 rounded-full font-medium">
                          {position.department}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1.5">
                          <FaMapMarkerAlt className="text-micropoint-gold text-xs" /> {position.location}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FaBriefcase className="text-micropoint-gold text-xs" /> {position.type}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FaClock className="text-micropoint-gold text-xs" /> {position.posted}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FaBuilding className="text-micropoint-gold text-xs" /> {position.experience}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="text-sm"
                        onClick={() => toggleExpand(index)}
                      >
                        {expandedPosition === index ? 'Hide Details' : 'View Details'}
                      </Button>
                      <Button className="text-sm">Apply Now</Button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedPosition === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4 pt-4 border-t border-gray-100"
                    >
                      <p className="text-gray-600 text-sm leading-relaxed">{position.description}</p>
                      <div className="flex flex-wrap gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Location:</span> {position.location}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Type:</span> {position.type}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="font-medium text-gray-700">Experience:</span> {position.experience}
                        </div>
                      </div>
                      <Button className="mt-4 text-sm">
                        Apply Now
                      </Button>
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Internship Program */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gradient-to-r from-micropoint-blue to-blue-700 rounded-2xl p-8 md:p-10 text-center text-white relative overflow-hidden"
        >
          {/* Decorative element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="text-5xl mb-4">🚀</div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 font-display">Internship Program 2026</h2>
            <p className="text-gray-200 mb-6 max-w-2xl mx-auto text-sm md:text-base">
              Kickstart your tech career at Micropoint Systems. Internships available in Software Development, 
              QA, ERP Consulting, Business Analysis, Mobile Development, and Data Science.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {['Software Development', 'QA Testing', 'ERP Consulting', 'Business Analysis', 'Mobile Development', 'Data Science'].map((role, i) => (
                <span key={i} className="bg-white/10 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full border border-white/10">
                  {role}
                </span>
              ))}
            </div>
            <Link to="/careers/internship">
              <Button className="bg-micropoint-gold hover:bg-yellow-500 text-white px-8 py-3.5 rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2">
                Apply for Internship <FaArrowRight />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Perks & Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 md:mt-16 bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm"
        >
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-6 font-display">
            Perks & <span className="text-micropoint-gold">Benefits</span>
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: FaHeart, title: 'Health Insurance', description: 'Comprehensive medical coverage' },
              { icon: FaGraduationCap, title: 'Learning Budget', description: 'Annual training & development' },
              { icon: FaGlobeAfrica, title: 'Remote Work', description: 'Flexible work arrangements' },
              { icon: FaUsers, title: 'Team Events', description: 'Regular team building activities' },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all">
                  <div className="w-10 h-10 rounded-full bg-micropoint-gold/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="text-micropoint-gold text-lg" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
}